import { forwardRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
//import { format } from 'date-fns';
import DatePicker from 'react-datepicker';
import TimePicker from '../../../components/Picker/TimePicker';
import RadioCheck from '../../../components/MessageManager/RadioCheck';
import dateicon from '../../../assets/icons/message-manager/date.svg';
import commonFunctions from '../../../utils/commonFunctions';
import { SIGNAGE_MEDIA } from '../../../utils/constants';

const ScheduleDateTimePanel = ({ scheduler, setScheduler, startingDateValid, startingTimeValid, endingDateValid, endingTimeValid, recurringValid, validated, markUnsavedChanges }) => {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });
  const [timePickerLayout] = useState("vertical-columns"); // "grid" or "vertical-columns"
  // If creating a new signage, there are no restrictions on the start and end times at the beginning.
  const now = new Date();
  if (!scheduler.startingTime) scheduler.startingTime = new Date(1970, 0, 1, now.getHours(), now.getMinutes(), 0);
  if (!scheduler.endingTime) scheduler.endingTime = new Date(1970, 0, 1, 23, 59, 0);
  const [startMinTime, setStartMinTime] = useState(new Date(1970, 0, 1, 0, 0, 0));
  const [startMaxTime, setStartMaxTime] = useState(new Date(1970, 0, 1, scheduler.endingTime?.getHours() ?? 23, scheduler.endingTime?.getMinutes() ?? 59, 0));
  const [endMinTime, setEndMinTime] = useState(new Date(1970, 0, 1, scheduler.startingTime?.getHours() ?? 0, scheduler.startingTime?.getMinutes() ?? 0, 0));
  const [endMaxTime, setEndMaxTime] = useState(new Date(1970, 0, 1, 23, 59, 0));

  // CustomInput component for DatePicker
  const CustomInput = forwardRef(({ value, onClick, icon, placeholderText }, ref) => (
    <div className="position-relative">
      <input
        type="text"
        className="form-control"
        onClick={onClick}
        ref={ref}
        value={value || ''}
        readOnly
        placeholder={placeholderText}
      />
      <img src={icon} className="calendar-icon" />
    </div>
  ));

  // date selection selection
  const scheduleDate = (label, value, onChange, minDate, maxDate, valid, colClass = "col-6") => (
    <div className={`${colClass} position-relative schedule-datetime`}>
      <label className="font-weight-600 font-size-14 mb-2"><FormattedMessage id={label} />:</label>
      <DatePicker
        selected={value ? new Date(value) : null}
        onChange={onChange}
        dateFormat="M/d/yyyy"
        minDate={minDate}
        maxDate={maxDate}
        customInput={<CustomInput icon={dateicon} placeholderText={t("SELECT_DATE")} />}
        wrapperClassName={`${validated && (!valid ? 'is-valid' : 'is-invalid')}`}
      />
      {commonFunctions.InvalidFeedbackString(valid, label)}
    </div>
  );

  const setTimePickerMinAndMax = (isStartingTime, newTime) => {
    if (newTime) {
      if (!isStartingTime) {
        setStartMaxTime(newTime);
      } else {
        setEndMinTime(newTime);
      }
    }
  }

  const handleTimeChange = (label, onChange, value) => {
    if (!value || value.trim() === '') return;

    const isStartingTime = label === "STARTING_TIME";
    const [timePart, period] = value.split(' ');
    let [hours, minutes] = timePart.split(':').map(Number);

    minutes = Math.min(Math.max(minutes, 0), 59);
    if (period === 'PM' && hours !== 12) {
      hours += 12;
    } else if (period === 'AM' && hours === 12) {
      hours = 0;
    }

    const newTime = new Date(1970, 0, 1, hours, minutes, 0);
    setTimePickerMinAndMax(isStartingTime, newTime);
    if (typeof onChange === 'function') onChange(newTime);
  };

  const scheduleTime = (label, value, onChange, minTime, maxTime, valid, colClass = "col-6") => {
    // const displayTime = format(value, 'hh:mm aa');
    return (
      <div className={`${colClass} position-relative schedule-datetime`}>
        <label className="font-weight-600 font-size-14 mb-2"><FormattedMessage id={label} /></label>
        <div className={`position-relative`}>
          <TimePicker
            label={label}
            className={`${validated && (!valid ? 'is-valid' : 'is-invalid')}`}
            placeholder={t("SELECT_TIME")}
            value={value}
            minuteInterval={15}
            onChange={handleTimeChange.bind(null, label, onChange)}
            minTime={minTime}
            maxTime={maxTime}
            validated={validated}
            valid={valid}
            layout={timePickerLayout}
          />
          {/*
          <div style={{ marginTop: '20px' }}>
            <p>Current selected time: {displayTime}</p>
            <p>Start min and max {format(startMinTime, 'hh:mm aa')} ~ {format(startMaxTime, 'hh:mm aa')}</p>
            <p>End min and max {format(endMinTime, 'hh:mm aa')} ~ {format(endMaxTime, 'hh:mm aa')}</p>
          </div>
          */}
        </div>
      </div>
    );
  };
  // Repeat change handler
  const handleRepeatChange = (e) => {
    setScheduler(prev => ({
      ...prev,
      repeat: e.target.value
    }));
  };

  // Cycle day switch
  const handleDayToggle = (index) => {
    setScheduler(prev => {
      const arr = [...prev.recurringDays];
      arr[index] = !arr[index];
      return { ...prev, recurringDays: arr };
    });
  };

  // Render recurring days
  const renderRecurringDays = () => (
    <div className="col-6 pe-2 position-relative schedule-datetime">
      <div className="d-flex">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, index) => (
          <button
            key={day}
            type="button"
            className={`btn btn-sm me-3 ${scheduler.recurringDays[index] ? 'btn-primary selected-day' : 'btn-outline-primary unselected-day'}`}
            onClick={() => {handleDayToggle(index); markUnsavedChanges && markUnsavedChanges();}}
          >
            {day}
          </button>
        ))}
      </div>
      {validated && commonFunctions.InvalidFeedbackString(recurringValid, '', "d-block")}
    </div>
  );

  return (
    <div className="mt-3">
      <label className="font-weight-600 font-size-14"><FormattedMessage id="SCHEDULE_SIGNAGE_HINT" /></label>
      <div className="row mb-3 mt-3">
        {scheduleDate(
          "STARTING_DATE",
          scheduler.startingDate,
          date => {setScheduler(prev => ({ ...prev, startingDate: new Date(date) })); markUnsavedChanges && markUnsavedChanges();},
          new Date(),
          scheduler.endingDate || undefined,
          startingDateValid,
          "col-6 pe-2"
        )}
        {scheduleDate(
          "ENDING_DATE",
          scheduler.endingDate,
          date => {setScheduler(prev => ({ ...prev, endingDate: new Date(date) })); markUnsavedChanges && markUnsavedChanges();},
          scheduler.startingDate || new Date(),
          undefined,
          endingDateValid,
          "col-6 ps-2"
        )}
      </div>
      {/* Repeat Radio button area */}
      <div className="row mb-3 mt-3 align-items-center">
        <label className="font-weight-600 font-size-14"><FormattedMessage id="REPEAT" />:</label>
        <div className='mt-2'>
          <RadioCheck
            options={{ [SIGNAGE_MEDIA.CREATE_SIGNAGE.DAILY]: "DAILY", [SIGNAGE_MEDIA.CREATE_SIGNAGE.RECURRING]: "CUSTOM_DAYS" }}
            name="repeat"
            selectedOption={scheduler.repeat}
            onChange={e => { handleRepeatChange(e); markUnsavedChanges && markUnsavedChanges(); }}
            inline
          />
        </div>
      </div>
      {scheduler.repeat === SIGNAGE_MEDIA.CREATE_SIGNAGE.RECURRING && (
        <div className="row mb-3">
          {renderRecurringDays()}
        </div>
      )}
      <div className="row mb-3 mt-3">
        {scheduleTime(
          "STARTING_TIME",
          scheduler.startingTime,
          time => {setScheduler(prev => ({ ...prev, startingTime: new Date(time) })); markUnsavedChanges && markUnsavedChanges();},
          startMinTime,
          startMaxTime,
          startingTimeValid,
          "col-6 pe-2"
        )}
        {scheduleTime(
          "ENDING_TIME",
          scheduler.endingTime,
          time => {setScheduler(prev => ({ ...prev, endingTime: new Date(time) })); markUnsavedChanges && markUnsavedChanges();},
          endMinTime,
          endMaxTime,
          endingTimeValid,
          "col-6 ps-2"
        )}
      </div>
    </div>
  );
};

export default ScheduleDateTimePanel;
